import { Component, inject, signal } from '@angular/core';
import { IonContent, IonHeader, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { lastValueFrom } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { WorkWebsiteFormsV2Service } from '@work-frontend-monorepo/work-rest-api';
import { ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { InputFieldComponent } from '@work-frontend-monorepo/work-ui/forms';
import { ExceptionHandlingService, LoggerService } from '@work-frontend-monorepo/work-ui/services';
import { FormWizardComponent, FormWizardStepDirective } from '@work-frontend-monorepo/work-ui/wizard';
import { environment } from '@work-frontend-monorepo/work-environments';

interface CapturedPhoto {
  dataUrl: string;
  blob: Blob;
  fileName: string;
}

interface GeruestanfrageForm {
  strasse: FormControl<string>;
  plz: FormControl<string>;
  ort: FormControl<string>;
  vorname: FormControl<string>;
  nachname: FormControl<string>;
  email: FormControl<string>;
  telefon: FormControl<string>;
}

@Component({
  selector: 'sfg-app-geruestanfrage-page',
  templateUrl: './geruestanfrage-page.component.html',
  styleUrls: ['./geruestanfrage-page.component.scss'],
  standalone: true,
  imports: [
    IonContent,
    IonHeader,
    IonTitle,
    IonToolbar,
    ReactiveFormsModule,
    TranslatePipe,
    ButtonComponent,
    InputFieldComponent,
    FormWizardComponent,
    FormWizardStepDirective,
  ],
})
export class GeruestanfragePageComponent {
  private readonly workWebsiteFormsV2Service = inject(WorkWebsiteFormsV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);

  capturedPhotos = signal<CapturedPhoto[]>([]);
  showConfirmation = signal(false);
  isSubmitting = signal(false);

  form = new FormGroup<GeruestanfrageForm>({
    strasse: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    plz: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    ort: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    vorname: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    nachname: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    email: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    telefon: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
  });

  protected async takePhoto() {
    try {
      await Camera.requestPermissions({ permissions: ['camera'] });
    } catch (notImplemented) {
      LoggerService.ERROR(this, 'notImplemented', notImplemented);
    }
    await this.pickPhoto(CameraSource.Camera);
  }

  protected async pickFromGallery() {
    try {
      await Camera.requestPermissions({ permissions: ['photos'] });
    } catch (notImplemented) {
      LoggerService.ERROR(this, 'notImplemented', notImplemented);
    }
    await this.pickPhoto(CameraSource.Photos);
  }

  protected removePhoto(index: number) {
    this.capturedPhotos.update((photos) => photos.filter((_, i) => i !== index));
  }

  protected async submitForm() {
    if (this.isSubmitting() || this.form.invalid) return;

    this.isSubmitting.set(true);
    try {
      const { strasse, plz, ort, vorname, nachname, email, telefon } = this.form.getRawValue();
      await lastValueFrom(
        this.workWebsiteFormsV2Service.websiteFormsV2ControllerSendForm({
          'x-api-key': environment.apiConfiguration.publicApiKey,
          body: {
            formName: 'sfg-app-geruestanfrage',
            data: JSON.stringify({ strasse, plz, ort, vorname, nachname, email, telefon }),
            files: this.capturedPhotos().map((p) => p.blob),
          },
        }),
      );
      this.showConfirmation.set(true);
    } catch (submitException) {
      this.exceptionHandlingService.handleExceptionWithAlert('geruestanfrageSubmitException', submitException);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async pickPhoto(source: CameraSource) {
    try {
      const photo: Photo = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source,
        saveToGallery: false,
      });

      if (!photo.dataUrl) return;

      const blob = await this.dataUrlToBlob(photo.dataUrl);
      const fileName = `geruestanfrage_${Date.now()}.jpg`;
      this.capturedPhotos.update((photos) => [...photos, { dataUrl: photo.dataUrl!, blob, fileName }]);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      if (!message?.toLowerCase().includes('cancel') && !message?.toLowerCase().includes('dismissed')) {
        this.exceptionHandlingService.handleExceptionWithAlert('pickPhotoException', error);
      }
    }
  }

  private async dataUrlToBlob(dataUrl: string): Promise<Blob> {
    const response = await fetch(dataUrl);
    return response.blob();
  }
}
